// @greenlight-spec: 11-observability.md#worker-task-status
// @greenlight-spec: 27-fleet-health-monitor.md
import { z } from 'zod';
/** A Graphile task identifier: dotted snake_case segments (`health.collect_snapshot`). */
export const WORKER_TASK_IDENTIFIER_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/;
export const WorkerTaskIdentifierSchema = z.string().max(96).regex(WORKER_TASK_IDENTIFIER_PATTERN);
export const WORKER_TASK_OUTCOMES = ['succeeded', 'failed'];
export const WorkerTaskOutcomeSchema = z.enum(WORKER_TASK_OUTCOMES);
/** More than every scheduled task the worker registers, so a new one never trips the bound. */
export const WORKER_TASK_SUMMARIES_MAX = 64;
/**
 * One scheduled task as the snapshot and the fleet report carry it. No error
 * text crosses: the summary says that a task is failing, the `worker_task`
 * failure log says why. A task that has never run has a `null` outcome.
 */
export const WorkerTaskSummarySchema = z
    .object({
    task: WorkerTaskIdentifierSchema,
    last_outcome: WorkerTaskOutcomeSchema.nullable(),
    last_succeeded_at: z.string().datetime({ offset: true }).nullable(),
    consecutive_failures: z.number().int().nonnegative(),
})
    .strict();
export const WorkerTaskSummariesSchema = z
    .array(WorkerTaskSummarySchema)
    .max(WORKER_TASK_SUMMARIES_MAX);
/**
 * What Internal Health calls each scheduled task (docs/28 §`/internal-health`):
 * the work it does, never its identifier, which a reader cannot act on.
 */
export const WORKER_TASK_LABELS = {
    'metrics.collect_app': 'Collecting app metrics',
    'metrics.prune_app': 'Clearing old app metrics',
    'health.collect_snapshot': 'Recording control-plane health',
    'health.prune_snapshots': 'Clearing old health records',
    'health.backfill_metric_history': 'Filling health history',
    'health.forward_fleet_report': 'Sending the health report to Shift',
    'health.evaluate_fleet_reporting': 'Checking health reporting to Shift',
    'billing.evaluate_entitlement': 'Checking the subscription',
    'billing.report_usage': 'Reporting marketplace usage',
    'crashloop.detect': 'Watching for crash-looping apps',
    'anomaly.detect': 'Watching for unusual app traffic',
    'release_validation.reconcile': 'Checking release validation',
    'usage.accumulate_app_minutes': 'Counting app running time',
    'usage.prune': 'Clearing old usage records',
    'usage.close_month': 'Closing the monthly usage record',
    'usage.sync_cursor_tokens': 'Reading Cursor build token usage',
    'lifecycle.scan': 'Checking for unused apps',
    'certificate.ensure': 'Keeping the control-plane certificate in place',
    'certificate.await_delegation': 'Requesting the certificate once DNS is delegated',
    'install.bootstrap_route.reassert': 'Keeping the setup page closed',
    'registry.prune': 'Clearing old app images',
    'rate_limits.prune': 'Clearing old rate-limit counts',
    'audit.prune': 'Clearing old audit log entries',
    'notification.connection_requests.remind': 'Reminding about approved connections',
    'notification.weekly_digest': 'Sending the weekly digest',
    'apps.re_render_sweep': 'Refreshing every app after a release',
};
export const WORKER_TASK_FALLBACK_LABEL = 'A scheduled background job';
export function workerTaskLabel(task) {
    return WORKER_TASK_LABELS[task] ?? WORKER_TASK_FALLBACK_LABEL;
}
/** A scheduled task Queue health lists: one that is failing or overdue, never a healthy one. */
export const HealthWorkerTaskAttentionSchema = z.object({
    label: z.string(),
    state: z.enum(['failing', 'stale']),
    /** Failing: when the streak's first failed run started. Stale: the last success, or `null` if never. */
    since: z.string().datetime({ offset: true }).nullable(),
    consecutive_failures: z.number().int().nonnegative(),
    /** Opens the task's latest failure log; `null` when none is kept. */
    failure_log_id: z.string().uuid().nullable(),
});
export const HealthWorkerTasksSchema = z.object({
    items: z.array(HealthWorkerTaskAttentionSchema),
});
export const HealthWorkerTaskFailureLogSchema = z.object({
    label: z.string(),
    failed_at: z.string().datetime({ offset: true }),
    attempt: z.number().int().positive(),
    text: z.string(),
    truncated: z.boolean(),
});
//# sourceMappingURL=worker-tasks.js.map